import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiUpload, FiX } from "react-icons/fi";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { submitSociety } from "./APIs/CreateSocietyApi";

const CreateSociety = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [formData, setFormData] = useState({
    societyName: "",
    societyArea: "",
    societyAddress: "",
    societyAddress2: "",
    societyAddress3: "",
    societyRegistrationNo: "",
    reraRegistrationNo: "",
    societyEmail: "",
    societyPhoneNo: "",
    state: "",
    pinCode: "",
    contactPerson: "",
    contactPersonPhoneNo: "",
    countryCode: "+91",
    societyImage: null,
  });

  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "societyName",
      "societyArea",
      "societyAddress",
      "societyRegistrationNo",
      "societyEmail",
      "societyPhoneNo",
      "state",
      "pinCode",
      "contactPerson",
      "contactPersonPhoneNo",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) newErrors[field] = "Required";
    });

    if (formData.pinCode && !/^\d{6}$/.test(formData.pinCode))
      newErrors.pinCode = "Invalid PIN";
    if (
      formData.societyEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.societyEmail)
    )
      newErrors.societyEmail = "Invalid Email";
    if (
      formData.contactPersonPhoneNo &&
      !/^\d{7,15}$/.test(formData.contactPersonPhoneNo)
    )
      newErrors.contactPersonPhoneNo = "Invalid Phone";

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setPreviewImage(reader.result);
        reader.readAsDataURL(file);
      } else {
        setPreviewImage(null);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please fix validation errors.");
      return;
    }

    try {
      setLoading(true);
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== "")
          data.append(key, formData[key]);
      });

      const { societyImage, ...textData } = formData;
      const response = await submitSociety(textData, societyImage);

      if (response.data && response.data.includes("Save Successfully")) {
        toast.success("Society Created!");
        navigate("/SocietyList");
      }
      const resultMessage =
        typeof response === "string" ? response : response?.data;

      if (
        resultMessage &&
        resultMessage.toLowerCase().includes("save successfully")
      ) {
        toast.success("Society Created Successfully");
        setTimeout(() => navigate("/SocietyList"), 1500);
      } else {
        toast.error(response?.data);
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Server error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (
    label,
    name,
    placeholder,
    type = "text",
    required = false,
  ) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-bold text-text-muted uppercase tracking-wider">
        {label} {required && <span className="text-signal-orange">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={placeholder}
        className={`w-full bg-bg-input border ${errors[name] ? "border-red-500" : "border-border"} rounded px-4 py-3 text-text-main focus:outline-none focus:border-signal-orange transition-colors placeholder:text-text-muted/50`}
      />
      {errors[name] && (
        <span className="text-xs text-red-500">{errors[name]}</span>
      )}
    </div>
  );

  return (
    <div className="flex min-h-screen bg-bg-primary text-text-main font-satoshi transition-colors duration-300">
      <Sidebar />
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <Header />

        <main className="flex-1 overflow-y-auto p-6 md:p-12">
          <div className="max-w-5xl mx-auto">
            <div className="mb-10 flex justify-between items-center border-b border-border pb-6">
              <div>
                <h1 className="text-3xl font-clash font-bold text-text-main">
                  Initialize Society
                </h1>
                <p className="text-text-muted mt-1">
                  Register a new residential complex into the system
                </p>
              </div>
              <button
                onClick={() => navigate("/SocietyList")}
                className="text-text-muted hover:text-text-main flex items-center gap-2 transition-colors"
              >
                Cancel <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="bg-bg-secondary border border-border rounded-lg p-6">
                <h2 className="text-lg font-clash font-bold text-text-main mb-6 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-signal-orange"></span>
                  Basic Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderInput(
                    "Society Name",
                    "societyName",
                    "Ex: Green Valley",
                    "text",
                    true,
                  )}
                  {renderInput(
                    "Society Area",
                    "societyArea",
                    "Ex: Sector 42",
                    "text",
                    true,
                  )}
                  {renderInput(
                    "Registration No",
                    "societyRegistrationNo",
                    "Reg No",
                    "text",
                    true,
                  )}
                  {renderInput(
                    "RERA No",
                    "reraRegistrationNo",
                    "Start with P...",
                    "text",
                  )}

                  <div className="md:col-span-2 space-y-2">
                    <label className="text-xs font-bold text-text-muted uppercase tracking-wider">
                      Society Image
                    </label>
                    <div
                      className={`border-2 border-dashed ${previewImage ? "border-signal-lime" : "border-border"} rounded-lg p-8 flex flex-col items-center justify-center bg-bg-tertiary/50 hover:bg-bg-tertiary transition-colors cursor-pointer relative`}
                    >
                      <input
                        type="file"
                        name="societyImage"
                        accept="image/*"
                        onChange={handleChange}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      {previewImage ? (
                        <div className="relative">
                          <img
                            src={previewImage}
                            alt="Preview"
                            className="h-32 object-contain"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(null);
                              setFormData((prev) => ({
                                ...prev,
                                societyImage: null,
                              }));
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <FiX size={16} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <FiUpload className="text-2xl text-text-muted mb-2" />
                          <span className="text-sm text-text-muted">
                            Click to upload image
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="bg-bg-secondary border border-border rounded-lg p-6">
                <h2 className="text-lg font-clash font-bold text-text-main mb-6 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-signal-lime"></span>
                  Address Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    {renderInput(
                      "Primary Address",
                      "societyAddress",
                      "Street / Building",
                      "text",
                      true,
                    )}
                  </div>
                  {renderInput("Address Line 2", "societyAddress2", "Optional")}
                  {renderInput("Address Line 3", "societyAddress3", "Optional")}
                  {renderInput("State", "state", "State Name", "text", true)}
                  {renderInput("PIN Code", "pinCode", "000000", "text", true)}
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="bg-bg-secondary border border-border rounded-lg p-6">
                <h2 className="text-lg font-clash font-bold text-text-main mb-6 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                  Contact Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {renderInput(
                    "Email",
                    "societyEmail",
                    "society@example.com",
                    "email",
                    true,
                  )}
                  {renderInput(
                    "Phone",
                    "societyPhoneNo",
                    "Official Contact",
                    "text",
                    true,
                  )}
                  {renderInput(
                    "Contact Person",
                    "contactPerson",
                    "Manager Name",
                    "text",
                    true,
                  )}

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-text-muted uppercase tracking-wider">
                      Person Phone <span className="text-signal-orange">*</span>
                    </label>
                    <div className="flex gap-2">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        className="bg-bg-input border border-border rounded px-3 text-text-main outline-none focus:border-signal-orange transition-colors"
                      >
                        <option value="+91">+91</option>
                        <option value="+1">+1</option>
                        <option value="+44">+44</option>
                      </select>
                      <input
                        type="text"
                        name="contactPersonPhoneNo"
                        value={formData.contactPersonPhoneNo}
                        onChange={handleChange}
                        placeholder="Phone Number"
                        className={`flex-1 bg-bg-input border ${errors.contactPersonPhoneNo ? "border-red-500" : "border-border"} rounded px-4 py-3 text-text-main focus:outline-none focus:border-signal-orange transition-colors placeholder:text-text-muted/50`}
                      />
                    </div>
                    {errors.contactPersonPhoneNo && (
                      <span className="text-xs text-red-500">
                        {errors.contactPersonPhoneNo}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end items-center pt-6 border-t border-border gap-4">
                <button
                  type="button"
                  onClick={() => navigate("/SocietyList")}
                  className="px-8 py-3 text-text-muted hover:text-text-main font-bold uppercase tracking-wide transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-signal-orange text-noir-900 font-bold uppercase tracking-wide hover:bg-orange-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Creating Society..." : "Initialize Society"}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CreateSociety;
